import React, { useMemo, useCallback, useState, createContext, useContext, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import {
	BellIcon,
	ChatAlt2Icon,
	ClockIcon,
	DownloadIcon,
	PlusSmIcon,
	ViewListIcon,
} from '@heroicons/react/outline';
import clsx from 'clsx';
import { useQueryParam, StringParam } from 'use-query-params';
import Notification from '@appmaker/components/ReactHotToastIntegrator';
import NewNotification from '../components/NewNotification';
import NotificationHistory from '../components/NotificationHistory';
import AppInstallCampaign from '../components/AppInstallCampaign';
import Campaign from '../components/Campaign';
import CampaignInsights from '../components/CampaignInsights';
import { useProject } from '@appmaker/core/hooks';

// Memoized navigation array to prevent recreation
const navigation = [
	{ name: 'New Notification', icon: BellIcon, slug: 'new-notification' },
	{ name: 'Notification History', icon: ClockIcon, slug: 'history' },
	{ name: 'App Install Campaign', icon: PlusSmIcon, slug: 'app-install' },
	{ name: 'Campaign', icon: ChatAlt2Icon, slug: 'campaigns' },
	{ name: 'Campaign Insights', icon: ViewListIcon, slug: 'insights' },
];

// Create context for tab navigation
const TabNavigationContext = createContext();

// Hook to use tab navigation
export const useTabNavigation = () => {
	const context = useContext(TabNavigationContext);
	if (!context) {
		throw new Error('useTabNavigation must be used within TabNavigationProvider');
	}
	return context;
};

// Memoized tab button component
const TabButton = React.memo(({ item, selected }) => (
	<button
		className={clsx(
			selected
				? 'bg-gray-100 text-gray-800'
				: 'text-gray-700 hover:text-gray-600 hover:bg-gray-50',
			'group w-full flex items-center gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold'
		)}
	>
		<item.icon
			className={clsx(
				selected
					? 'text-gray-500'
					: 'text-gray-400 group-hover:text-gray-500',
				'flex-shrink-0 h-5 w-5'
			)}
			aria-hidden="true"
		/>
		<span className="flex-1 text-left truncate">{item.name}</span>
	</button>
));

TabButton.displayName = 'TabButton';

// Memoized tab panels to prevent unnecessary re-renders
const TabPanels = React.memo(({ projectId }) => (
	<Tab.Panels
		as="div"
		className="col-span-10 h-screen overflow-y-auto bg-white"
	>
		<Tab.Panel key={'New Notification'}>
			<NewNotification projectId={projectId} />
		</Tab.Panel>
		<Tab.Panel key={'Notification History'}>
			<NotificationHistory projectId={projectId} />
		</Tab.Panel>
		<Tab.Panel key={'App Install Campaign'}>
			<AppInstallCampaign projectId={projectId} />
		</Tab.Panel>
		<Tab.Panel key={'Campaign'}>
			<Campaign projectId={projectId} />
		</Tab.Panel>
		<Tab.Panel key={'Campaign Insights'}>
			<CampaignInsights projectId={projectId} />
		</Tab.Panel>
	</Tab.Panels>
));

TabPanels.displayName = 'TabPanels';

export default function Home() {
	const { projectId } = useProject();
	
	// URL parameter for tab selection
	const [tabParam, setTabParam] = useQueryParam('tab', StringParam);
	
	// Local state for selected index
	const [selectedIndex, setSelectedIndex] = useState(0);

	// Memoized navigation list to prevent recreation
	const memoizedNavigation = useMemo(() => navigation, []);

	// Function to get tab index from slug
	const getTabIndexFromSlug = useCallback((slug) => {
		const index = navigation.findIndex(nav => nav.slug === slug);
		return index !== -1 ? index : 0;
	}, []);

	// Function to get slug from tab index
	const getSlugFromTabIndex = useCallback((index) => {
		return navigation[index]?.slug || navigation[0].slug;
	}, []);

	// Initialize selected index from URL parameter
	useEffect(() => {
		if (tabParam) {
			const index = getTabIndexFromSlug(tabParam);
			setSelectedIndex(index);
			console.log('Initialized tab from URL param:', tabParam, 'index:', index);
		} else {
			// Set default tab in URL if no parameter exists
			setTabParam(navigation[0].slug);
		}
	}, [tabParam, getTabIndexFromSlug, setTabParam]);

	// Function to navigate to a specific tab by name
	const navigateToTab = useCallback((tabName) => {
		console.log('navigateToTab called with:', tabName);
		const tabIndex = navigation.findIndex(nav => nav.name === tabName);
		console.log('Found tab index:', tabIndex);
		if (tabIndex !== -1) {
			const slug = navigation[tabIndex].slug;
			setTabParam(slug);
			setSelectedIndex(tabIndex);
			console.log('Set selected index to:', tabIndex, 'with slug:', slug);
		} else {
			console.error('Tab not found:', tabName);
		}
	}, [setTabParam]);

	// Function to navigate to a specific tab by slug
	const navigateToTabBySlug = useCallback((slug) => {
		console.log('navigateToTabBySlug called with:', slug);
		const tabIndex = getTabIndexFromSlug(slug);
		setTabParam(slug);
		setSelectedIndex(tabIndex);
		console.log('Set selected index to:', tabIndex, 'with slug:', slug);
	}, [getTabIndexFromSlug, setTabParam]);

	// Function to navigate to Campaign Insights specifically
	const navigateToCampaignInsights = useCallback(() => {
		console.log('navigateToCampaignInsights called');
		navigateToTabBySlug('insights');
	}, [navigateToTabBySlug]);

	// Handle tab change from Tab.Group
	const handleTabChange = useCallback((index) => {
		console.log('Tab.Group onChange called with index:', index);
		const slug = getSlugFromTabIndex(index);
		setTabParam(slug);
		setSelectedIndex(index);
		console.log('Updated URL param to:', slug);
	}, [getSlugFromTabIndex, setTabParam]);

	// Tab navigation context value
	const tabNavigationValue = useMemo(() => ({
		navigateToTab,
		navigateToTabBySlug,
		navigateToCampaignInsights,
		selectedIndex,
		setSelectedIndex,
		currentTabSlug: tabParam
	}), [navigateToTab, navigateToTabBySlug, navigateToCampaignInsights, selectedIndex, tabParam]);

	// Debug: Log when selectedIndex changes
	useEffect(() => {
		console.log('Selected tab index changed to:', selectedIndex);
		console.log('Current tab name:', navigation[selectedIndex]?.name);
		console.log('Current tab slug:', navigation[selectedIndex]?.slug);
		console.log('URL param:', tabParam);
	}, [selectedIndex, tabParam]);

	return (
		<Notification>
			<TabNavigationContext.Provider value={tabNavigationValue}>
				<Tab.Group 
					as="div" 
					className="grid grid-cols-12 divide-x divide-gray-100"
					selectedIndex={selectedIndex}
					onChange={handleTabChange}
				>
					<nav
						className="col-span-2 flex flex-1 flex-col p-4 h-screen overflow-y-auto"
						aria-label="Sidebar"
					>
						<Tab.List as="ul" className="-mx-2 space-y-1">
							{memoizedNavigation.map((item) => (
								<Tab as="li" key={item.name}>
									{({ selected }) => (
										<TabButton item={item} selected={selected} />
									)}
								</Tab>
							))}
						</Tab.List>
					</nav>

					<TabPanels projectId={projectId} />
				</Tab.Group>
			</TabNavigationContext.Provider>
		</Notification>
	);
}
